import { withAccessRestriction } from '@components/auth/withAccessRestriction';
import { DOCUMENTS_ACCESS_ROLES } from '@constants/auth.constants';
import { decodeToken } from '@utils/auth/decode-token.util';
import { getSessionCookie } from '@utils/auth/session.util';
import { EventEmitter, useAnalytics, useClarity } from '@vidacamara/orange-utils';
import { lazy, Suspense, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Route, Routes, useLocation } from 'react-router-dom';

const DocumentsModule = lazy(() => import('@views/documents/DocumentsModule'));
const DocumentDetail = lazy(() => import('@views/document-detail/DocumentDetail'));

const GuardedDocumentsModule = withAccessRestriction(DocumentsModule, DOCUMENTS_ACCESS_ROLES);

const APP_NAME = 'mf-policies-document';
const BASE_PATH = '/policies-document';

// useLocation only works INSIDE the Router, so page tracking lives in its own
// child component instead of AppRoutes (which renders the Router itself).
const PageViewTracker = () => {
	const location = useLocation();
	const { trackPageView } = useAnalytics();

	useEffect(() => {
		const path = location.pathname === '/' ? BASE_PATH : `${BASE_PATH}${location.pathname}`;
		trackPageView(path, { appName: APP_NAME });
	}, [location, trackPageView]);

	return null;
};

// Publishes the legacy breadcrumb_update contract so the shell header shows
// real segments instead of its loading skeleton.
const BreadcrumbPublisher = ({ eventEmitter }: { eventEmitter?: EventEmitter }) => {
	const location = useLocation();
	const { t } = useTranslation();

	useEffect(() => {
		if (!eventEmitter) return;
		const segments = [{ title: t('search.title'), href: BASE_PATH }];
		const policyId = location.pathname.replace(/^\//, '');
		if (policyId) segments.push({ title: policyId, href: `${BASE_PATH}/${policyId}` });
		eventEmitter.emit('breadcrumb_update', { state: 'has_segment_data', payload: { segments } });
	}, [location, eventEmitter, t]);

	return null;
};

interface AppRoutesProps {
	eventEmitter?: EventEmitter;
}

export const AppRoutes = ({ eventEmitter }: AppRoutesProps) => {
	const { t } = useTranslation();
	const { setUserId } = useAnalytics();
	const { setUserProperties } = useClarity();
	const originalToken = getSessionCookie();

	if (originalToken) {
		const { person } = decodeToken(originalToken);
		if (person) {
			setUserId(person);
			setUserProperties(person, { appName: APP_NAME });
		}
	}

	return (
		<BrowserRouter basename={BASE_PATH}>
			<PageViewTracker />
			<BreadcrumbPublisher eventEmitter={eventEmitter} />
			<Suspense fallback={<div>{t('common.loading')}</div>}>
				<Routes>
					<Route path="/">
						<Route index element={<GuardedDocumentsModule />} />
						<Route path=":policyId" element={<DocumentDetail />} />
					</Route>
				</Routes>
			</Suspense>
		</BrowserRouter>
	);
};
